// src/services/duplicateMoveService.js
//
// Scan for Duplicates' "Move to Duplicates" (2026-10-29), one record at a time,
// for both stores:
//
//   - A RegX patient (consents) moves the same way the patient page does when
//     its status is set to "Potential Duplicate": the WHOLE consents doc is
//     copied to duplicates/{same id} and the consents doc removed, so notes and
//     audit history (keyed by that id) stay with it, and the chart's status
//     picker restores it. Done in one transaction that re-reads both docs, so it
//     refuses if the patient is gone or a duplicates doc already has that id
//     (never overwritten).
//   - A Google Sheet patient (Dubois) only gets STATUS = "Potential Duplicate"
//     through markSheetPatientDuplicate. Nothing is copied or deleted; the row
//     keeps all its data and the chart's status picker undoes it.
//
// Who moved them and when MUST be on the patient's audit trail (user,
// 2026-10-29), so it is not fail-soft here like other audit lines: for a RegX
// patient the MOVED_TO_POTENTIAL_DUPLICATE event is written INSIDE the move's
// transaction (moved and recorded together, or neither); for a Sheet patient the
// server writes it right after the row, and this file writes it only if the
// server says it couldn't. Last 4 of the MRN only, as everywhere.

import { collection, doc, runTransaction, serverTimestamp } from "firebase/firestore";
import { db, auth } from "../firebase";
import { getConsentsPath, getDuplicatesPath, getPatientAuditEventsPath } from "./firestorePaths";
import { markSheetPatientDuplicate } from "./sheetPatientsService";
import { logPatientAuditEvent, getMrnLast4 } from "./patientAuditService";
import { POTENTIAL_DUPLICATE_STATUS as POTENTIAL_DUPLICATE } from "../constants/patientBoxes";

/** Thrown when the move was refused; `message` is safe to show. Nothing was changed. */
export class DuplicateMoveRefusedError extends Error {
  constructor(message) {
    super(message);
    this.name = "DuplicateMoveRefusedError";
  }
}

/** The duplicates doc for a consents doc: every field kept, plus status and who/when. */
export const duplicateDocFrom = (consData, { by, at }) => ({
  ...consData,
  status: POTENTIAL_DUPLICATE,
  movedToDuplicatesAt: at,
  movedToDuplicatesBy: by,
  movedToDuplicatesFrom: "DuplicateScan",
  lastEditedBy: by,
  lastEditedAt: at,
});

/** The patient-history event for a move (the same one the patient page writes). */
export const moveAuditEvent = ({ id, name, mrn, from, isSheet, by, uid }) => ({
  title: "Moved to Potential Duplicate",
  action: "MOVED_TO_POTENTIAL_DUPLICATE",
  patientId: id,
  patientName: name || "",
  mrnLast4: getMrnLast4(mrn),
  source: isSheet ? "sheet" : "firestore",
  actorEmail: by,
  actorUid: uid || null,
  type: "WRITE",
  severity: "warning",
  changes: [{ field: "status", from: from || null, to: POTENTIAL_DUPLICATE }],
  metadata: {
    page: "DuplicateScanPage",
    recordPath: isSheet ? `sheet/${id}` : `duplicates/${id}`,
    movedFrom: isSheet ? "sheet" : "consents",
  },
});

const moveFirestorePatient = async (record, by, uid) => {
  const id = record.id;
  const consRef = doc(db, getConsentsPath(), id);
  const dupRef = doc(db, getDuplicatesPath(), id);
  const auditRef = doc(collection(db, getPatientAuditEventsPath(id)));
  return runTransaction(db, async (tx) => {
    const [consSnap, dupSnap] = [await tx.get(consRef), await tx.get(dupRef)];
    if (!consSnap.exists()) {
      throw new DuplicateMoveRefusedError("This patient is no longer on the patient list. Scan again.");
    }
    if (dupSnap.exists()) {
      throw new DuplicateMoveRefusedError(
        "A Potential Duplicates record with this patient's id already exists, so nothing was moved. Open the chart to check."
      );
    }
    const consData = consSnap.data();
    const at = serverTimestamp();
    tx.set(dupRef, duplicateDocFrom(consData, { by, at }));
    tx.delete(consRef);
    tx.set(auditRef, {
      ...moveAuditEvent({
        id,
        name: record.name || consData.fullName || consData.name,
        mrn: consData.mrn ?? record.mrn,
        from: consData.status || record.status,
        isSheet: false,
        by,
        uid,
      }),
      createdAt: at,
    });
  });
};

/**
 * @param {{id: string, source: "firestore"|"sheet", status?: string, name?: string, mrn?: string}} record
 *   a Scan for Duplicates record (normalizePatientForMatching shape)
 */
export const moveToDuplicates = async (record) => {
  const id = record && record.id;
  if (!id) throw new DuplicateMoveRefusedError("This record has no id, so it can't be moved.");
  const by = auth.currentUser?.email || "unknown";
  const uid = auth.currentUser?.uid || null;

  if (record.source !== "sheet") {
    await moveFirestorePatient(record, by, uid);
    return;
  }

  let reply;
  try {
    reply = await markSheetPatientDuplicate({ token: id, expectStatus: record.status || "" });
  } catch (err) {
    // A definite "no" from the server (changed meanwhile, not allowed).
    if (err && err.name === "SheetWriteRefusedError") throw new DuplicateMoveRefusedError(err.message);
    throw err;
  }
  // The server records it; only if it couldn't, record it from here.
  if (!reply || reply.audited !== true) {
    await logPatientAuditEvent(
      moveAuditEvent({ id, name: record.name, mrn: record.mrn, from: record.status, isSheet: true, by, uid })
    );
  }
};
